import * as Sentry from "@sentry/react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { UnattributedDraftClaimGate } from "@/components/pwa/UnattributedDraftClaimGate";
import { AuthProvider } from "@/contexts/auth-context";
import { SurfaceProvider } from "@/contexts/surface-context";
import { AppRoutes } from "@/routes/app-routes";
import { buttonVariants } from "@/components/ui/button";

// Single app-wide query client. Conservative defaults for a clinical app: no refetch
// storms on window focus, one retry. No suspense (we render explicit loading states).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// Provider order (per architecture): BrowserRouter > QueryClientProvider > AuthProvider >
// SurfaceProvider > Routes. SurfaceProvider sits inside AuthProvider so SurfaceGuard
// (which runs after the auth/role guards) can read the classified install surface.
//
// ErrorFallback renders OUTSIDE BrowserRouter — Link/useNavigate are unavailable here.
// Use plain <a href="/"> and window.location.reload() for navigation.
function ErrorFallback({ eventId }: { eventId: string | null }) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-[color:var(--bg-secondary)] p-8">
      <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
        <div className="flex flex-col gap-2">
          <h1 className="display-xs text-[color:var(--text-primary)]">Something went wrong</h1>
          <p className="text-md text-[color:var(--text-tertiary)]">
            An unexpected error occurred. Please refresh the page. If the problem
            persists, contact support.
          </p>
          {eventId && (
            <p className="text-xs text-[color:var(--text-quaternary)]">
              Error ID: {eventId}
            </p>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={() => window.location.reload()}
            className={buttonVariants({ variant: "primary" })}
          >
            Refresh page
          </button>
          <a href="/" className={buttonVariants({ variant: "secondary" })}>
            Go to home
          </a>
        </div>
      </div>
    </main>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ eventId }) => <ErrorFallback eventId={eventId} />}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            {/* Franklin#861 — claim door for unattributed offline drafts; needs Auth + QueryClient. */}
            <UnattributedDraftClaimGate />
            <SurfaceProvider>
              <AppRoutes />
            </SurfaceProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
